import {
  coerceDate,
  coerceNumeric,
  parseExcelWorkbook,
} from "@1823-partners/core";

const OUTPUT_SHEET = "Output";
const HEADER_ROW_INDEX = 2;
const DATA_START_ROW_INDEX = 3;

const BBG_RAW_LEVEL_NAMES = new Set([
  "Freddie Mac Commitment",
  "CC MBS",
  "CC MBS OAZ",
  "CC MBS OAS",
  "MOVE Index",
]);

// The workbook appends a Treasury Curve and a SOFR Curve block below its
// asset rows (added 2026-08-24). They share the sheet but not its meaning: the
// level sits in col F rather than the spread column, and a tenor is not an
// asset. Section label -> the source type each block is stored under.
const CURVE_SECTIONS = {
  "Treasury Curve": "UST_CURVE",
  "SOFR Curve": "SOFR_CURVE",
};

// Curve rows use their own columns: C tenor, D source, E date, F level.
const CURVE_COL = {
  tenor: 2,
  source: 3,
  date: 4,
  value: 5,
};

const COL = {
  category: 1,
  shortName: 2,
  source: 3,
  date: 4,
  rating: 5,
  wal: 6,
  currentSpread: 7,
  assetLeverage: 8,
  frictionCost: 9,
  remainingC1: 10,
  baseRate: 11,
  impliedYield: 12,
  rbc: 13,
  spreadRbcCapital: 14,
  spCapital: 15,
  spreadSpCapital: 16,
  fixedFloating: 17,
  collateralWal: 18,
  spreadDuration: 19,
  notes: 20,
  wowChange: 21,
  historyMinDate: 22,
  range2025: 23,
  range3y: 24,
};

const cleanText = (value) => (value == null ? "" : String(value).trim());

const numericOrText = (value) => {
  const numeric = coerceNumeric(value);
  return numeric == null ? cleanText(value) || null : numeric;
};

const numericValue = (value, label, rowNumber) => {
  if (!cleanText(value)) return null;
  const numeric = coerceNumeric(value);
  if (numeric == null || !Number.isFinite(Number(numeric))) {
    throw new Error(
      `Invalid numeric value in ${label} at Output row ${rowNumber}`,
    );
  }
  return numeric;
};

// The Asset Dashboard has a sheet named 'Output' too — it is the master model
// the Menu is extracted from, carrying the same headers shifted right (Short
// Name at Z, not C, because the Menu drops the Dashboard's calculation-input
// columns). Uploading it otherwise fails as three unrelated missing columns,
// which does not hint that the wrong one of two similarly named workbooks was
// picked. Detected by the header the shift lands on.
const DASHBOARD_SHORT_NAME_INDEX = 25;

const validateOutputHeaders = (rows) => {
  const headers = rows[HEADER_ROW_INDEX] || [];

  if (cleanText(headers[DASHBOARD_SHORT_NAME_INDEX]) === "Short Name") {
    throw new Error(
      "This looks like the Asset Dashboard workbook, not the Rel Val Asset " +
        "Menu. The Dashboard is the master model and is not imported — pick " +
        "the dated extract instead (e.g. 20260901 Rel Val Asset Menu.xlsx).",
    );
  }

  const expected = [
    [COL.shortName, "Short Name"],
    [COL.source, "Source"],
    [COL.currentSpread, "Current Spread / Level / Yield"],
  ];

  const missing = expected
    .filter(([index, label]) => cleanText(headers[index]) !== label)
    .map(([, label]) => label);

  if (missing.length) {
    throw new Error(
      `The Output sheet is missing expected columns: ${missing.join(", ")}`,
    );
  }
};

/**
 * Convert the Asset Menu Output sheet into the row shape consumed by RelValGrid.
 * Section labels in column B are forward-filled into Asset Class/Sub-Class.
 */
export const mapAssetMenuOutputRows = (rows) => {
  validateOutputHeaders(rows);

  const imported = [];
  const curves = [];
  const identityCounts = new Map();
  let assetClass = "";
  let subClass = "";
  let curveType = null;
  let curveSection = "";

  for (let index = DATA_START_ROW_INDEX; index < rows.length; index += 1) {
    const row = rows[index] || [];
    const category = cleanText(row[COL.category]);
    const shortName = cleanText(row[COL.shortName]);
    const source = cleanText(row[COL.source]);

    // A section label and the block's first tenor share one row, so the curve
    // switch has to happen before the row itself is classified.
    if (category) {
      curveType = CURVE_SECTIONS[category] || null;
      curveSection = curveType ? category : "";
    }

    if (curveType) {
      const tenor = cleanText(row[CURVE_COL.tenor]);
      if (!tenor || !cleanText(row[CURVE_COL.source])) continue;
      const value = numericValue(
        row[CURVE_COL.value],
        `${curveSection} ${tenor}`,
        index + 1,
      );
      // VALUE is NOT NULL in the source table, and a blank tenor is a tenor
      // the curve simply does not publish this week rather than an error.
      if (value == null) continue;
      curves.push({
        sourceType: curveType,
        IDENTIFIER: tenor,
        VALUE: value,
        LAST_UPDATED_DATE: coerceDate(row[CURVE_COL.date]),
      });
      continue;
    }

    if (!shortName) {
      if (category) {
        assetClass = category;
        subClass = "";
      }
      continue;
    }

    if (!source) continue;
    if (category) subClass = category;

    const field =
      source === "BBG Index" && !BBG_RAW_LEVEL_NAMES.has(shortName)
        ? "INDEX_OAS_TSY"
        : null;
    const rowNumber = index + 1;
    const currentSpread = numericValue(
      row[COL.currentSpread],
      "Current Spread / Level / Yield",
      rowNumber,
    );
    const identityBase = [assetClass, subClass, shortName]
      .map((value) => encodeURIComponent(value))
      .join("|");
    const occurrence = (identityCounts.get(identityBase) || 0) + 1;
    identityCounts.set(identityBase, occurrence);

    imported.push({
      // Stable descriptive fields plus occurrence remain unchanged when
      // weekly values (rating, WAL, source, etc.) move, while disambiguating
      // intentionally repeated labels within one section.
      IDENTIFIER: `asset-menu:${identityBase}|${occurrence}`,
      ASSET_CLASS: assetClass || "(Uncategorized)",
      SUB_CLASS: subClass,
      SHORT_NAME: shortName,
      SOURCE: source,
      LAST_UPDATED_DATE: coerceDate(row[COL.date]),
      RATING: cleanText(row[COL.rating]) || null,
      WAL: numericOrText(row[COL.wal]),
      // Pending imports live in the same native units as server-loaded grid
      // rows. The snapshot payload restores workbook bps at the save boundary.
      CURRENT_SPREAD:
        field === "INDEX_OAS_TSY" && currentSpread != null
          ? currentSpread / 100
          : currentSpread,
      ASSET_LEVERAGE: numericValue(
        row[COL.assetLeverage],
        "Asset Leverage",
        rowNumber,
      ),
      FRICTION_COST_TO_TARGET: numericValue(
        row[COL.frictionCost],
        "Friction Cost to Target",
        rowNumber,
      ),
      REMAINING_C1_PERMITTED: numericValue(
        row[COL.remainingC1],
        "Remaining C1 Permitted",
        rowNumber,
      ),
      BASE_RATE: cleanText(row[COL.baseRate]) || null,
      IMPLIED_YIELD: numericValue(
        row[COL.impliedYield],
        "Implied Yield",
        rowNumber,
      ),
      RBC: numericValue(row[COL.rbc], "RBC", rowNumber),
      SPREAD_RBC_CAPITAL: numericValue(
        row[COL.spreadRbcCapital],
        "Spread / RBC Capital",
        rowNumber,
      ),
      SP_CAPITAL: numericValue(row[COL.spCapital], "S&P Capital", rowNumber),
      SPREAD_SP_CAPITAL: numericValue(
        row[COL.spreadSpCapital],
        "Spread / S&P Capital",
        rowNumber,
      ),
      FIXED_FLOATING: cleanText(row[COL.fixedFloating]) || null,
      COLLATERAL_WAL_STRUCT: numericOrText(row[COL.collateralWal]),
      SPREAD_DUR_RANGE: cleanText(row[COL.spreadDuration]) || null,
      BOND_STRUCT_NOTES: cleanText(row[COL.notes]) || null,
      WOW_SPREAD_CHANGE: numericValue(
        row[COL.wowChange],
        "WoW Spread Change",
        rowNumber,
      ),
      SPREAD_HISTORY_MIN_DATE: coerceDate(row[COL.historyMinDate]),
      RANGE_2025: cleanText(row[COL.range2025]) || null,
      RANGE_3Y: cleanText(row[COL.range3y]) || null,
      FIELD: field,
      ROW_ORDER: imported.length,
    });
  }

  if (!imported.length) {
    throw new Error("The Output sheet does not contain any RelVal data rows");
  }

  return { rows: imported, curves };
};

/**
 * Group parsed curve observations into one /save-relval-source payload each.
 *
 * `observed_date` is the date the block itself prints and is informational
 * only. The caller files each curve under the *snapshot's* as-of date, because
 * both the Asset Menu snapshot and the source observations are read with
 * "latest on or before the requested date": the 2026-09-01 workbook prints its
 * curves for 08-31 but files its snapshot under the internal-spread date of
 * 08-10, so a curve stored under its own later date would never be found for
 * the week it belongs to.
 */
export const groupAssetMenuCurves = (curves) => {
  const bySourceType = new Map();
  for (const curve of curves || []) {
    const existing = bySourceType.get(curve.sourceType) || {
      source_type: curve.sourceType,
      observed_date: null,
      rows: [],
    };
    existing.rows.push({ IDENTIFIER: curve.IDENTIFIER, VALUE: curve.VALUE });
    if (
      curve.LAST_UPDATED_DATE &&
      curve.LAST_UPDATED_DATE > (existing.observed_date || "")
    ) {
      existing.observed_date = curve.LAST_UPDATED_DATE;
    }
    bySourceType.set(curve.sourceType, existing);
  }
  return [...bySourceType.values()];
};

export const parseAssetMenuWorkbook = async (file) => {
  const { sheetsData } = await parseExcelWorkbook(file, {
    raw: true,
    defval: "",
  });
  const outputRows = sheetsData[OUTPUT_SHEET];

  if (!outputRows) {
    throw new Error('The workbook must contain a sheet named "Output"');
  }

  return mapAssetMenuOutputRows(outputRows);
};
